import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityFieldOption, EntityValue, EntityValuesUpdateBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import {
  clientEntitiesOptions,
  clientEntityGroupsOptions,
  errorMessage,
} from '@/features/clients/clients-queries';

interface DraftValue {
  value: string | null;
  optionId: number | null;
}

export function ClientEntitiesSection({ clientId }: { clientId: number }) {
  const queryClient = useQueryClient();
  const values = useQuery(clientEntitiesOptions(clientId));
  const groups = useQuery(clientEntityGroupsOptions());
  const [draft, setDraft] = useState<Record<number, DraftValue>>({});

  const optionsByField = useMemo(() => {
    const map = new Map<number, EntityFieldOption[]>();
    for (const g of groups.data ?? []) {
      for (const f of g.fields) map.set(f.id, f.options);
    }
    return map;
  }, [groups.data]);

  const grouped = useMemo(() => {
    const map = new Map<string, EntityValue[]>();
    for (const v of values.data ?? []) {
      const list = map.get(v.groupName);
      if (list) list.push(v);
      else map.set(v.groupName, [v]);
    }
    return [...map.entries()];
  }, [values.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body: EntityValuesUpdateBody = {
        values: Object.entries(draft).map(([fieldId, v]) => ({
          fieldId: Number(fieldId),
          value: v.value,
          optionId: v.optionId,
        })),
      };
      const res = await api<unknown>(`/clients/${clientId}/entities`, { method: 'PUT', body });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] });
    },
  });

  const setField = (original: EntityValue, next: DraftValue) => {
    setDraft((prev) => {
      const clone = { ...prev };
      const sameValue = (next.value ?? null) === (original.value ?? null);
      const sameOption = (next.optionId ?? null) === (original.optionId ?? null);
      if (sameValue && sameOption) delete clone[original.fieldId];
      else clone[original.fieldId] = next;
      return clone;
    });
  };

  const dirty = Object.keys(draft).length > 0;

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Campos personalizados</h2>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
      {values.isPending || groups.isPending ? (
        <div aria-busy className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      ) : values.isError ? (
        <p className="text-sm text-destructive">
          Erro ao carregar campos: {errorMessage(values.error)}
        </p>
      ) : grouped.length === 0 ? (
        <EmptyState className="h-24" message="Nenhum campo personalizado para clientes" />
      ) : (
        <div className="space-y-5">
          {grouped.map(([groupName, fields]) => (
            <div key={groupName}>
              <h3 className="mb-2 text-[13px] font-semibold text-muted-foreground">{groupName}</h3>
              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                {fields.map((v) => {
                  const current = draft[v.fieldId] ?? { value: v.value, optionId: v.optionId };
                  return (
                    <div key={v.fieldId} className="space-y-1.5">
                      <Label className="text-[13px]">
                        {v.fieldName}
                        {v.required ? <span className="text-destructive"> *</span> : null}
                      </Label>
                      <FieldControl
                        field={v}
                        current={current}
                        options={optionsByField.get(v.fieldId) ?? []}
                        onChange={(next) => setField(v, next)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {save.error ? (
        <p className="mt-3 text-sm text-destructive">{errorMessage(save.error)}</p>
      ) : null}
    </section>
  );
}

function FieldControl({
  field,
  current,
  options,
  onChange,
}: {
  field: EntityValue;
  current: DraftValue;
  options: EntityFieldOption[];
  onChange: (next: DraftValue) => void;
}) {
  switch (field.fieldType) {
    case 'text_area':
      return (
        <Textarea
          value={current.value ?? ''}
          onChange={(e) => onChange({ value: e.target.value || null, optionId: null })}
        />
      );
    case 'single_select':
      return (
        <Select
          className="h-8"
          value={current.optionId != null ? String(current.optionId) : ''}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null;
            const opt = id != null ? options.find((o) => o.id === id) : undefined;
            onChange({ value: opt?.value ?? null, optionId: id });
          }}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.value}
            </option>
          ))}
        </Select>
      );
    case 'checkbox':
      return (
        <div className="flex h-8 items-center">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={current.value === 'true'}
            onChange={(e) =>
              onChange({ value: e.target.checked ? 'true' : 'false', optionId: null })
            }
          />
        </div>
      );
    default: {
      const type =
        field.fieldType === 'date'
          ? 'date'
          : field.fieldType === 'phone'
            ? 'tel'
            : field.fieldType === 'email'
              ? 'email'
              : field.fieldType === 'link'
                ? 'url'
                : 'text';
      return (
        <Input
          type={type}
          className="h-8"
          value={current.value ?? ''}
          placeholder={field.fieldType === 'currency' ? 'R$ 0,00' : undefined}
          onChange={(e) => onChange({ value: e.target.value || null, optionId: null })}
        />
      );
    }
  }
}
